import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { InputComponent } from '../core/controls/input.component';
import { SelectComponent } from '../core/controls/select.component';
import { normalizeQueryMajorLiabilityParametersResponse } from './query-extended-params.form';
import type {
  QueryMajorCodeOption,
  QueryMajorCoverageOption,
  QueryMajorLiabilityParametersResponse,
} from '../../models/api/query/query-parameters';

type LiabilityTab = 'types' | 'codes';

@Component({
  selector: 'app-query-major-liability-lob',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SelectComponent, InputComponent, ButtonComponent],
  template: `
    <div class="tw-space-y-4 tw-max-w-[960px]">
      <h3 class="tw-font-semibold tw-text-lg">{{ title }}</h3>
      <div
        class="tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-[var(--sed-black-60-color)]"
        role="tablist"
        [attr.aria-label]="title + ' query options'"
      >
        <button
          type="button"
          role="tab"
          [attr.aria-selected]="tab === 'types'"
          class="tw-px-3 tw-py-2 tw-text-sm tw-rounded-t tw-border tw-mb-[-1px] tw-transition-colors"
          [class.tw-border-[var(--sed-black-60-color)]]="tab === 'types'"
          [class.tw-border-b-white]="tab === 'types'"
          [class.tw-bg-white]="tab === 'types'"
          [class.tw-font-semibold]="tab === 'types'"
          [class.tw-border-transparent]="tab !== 'types'"
          [class.tw-text-[var(--sed-black-60-color)]]="tab !== 'types'"
          (click)="tab = 'types'"
        >
          Claim Type/Coverage
        </button>
        <button
          type="button"
          role="tab"
          [attr.aria-selected]="tab === 'codes'"
          class="tw-px-3 tw-py-2 tw-text-sm tw-rounded-t tw-border tw-mb-[-1px] tw-transition-colors"
          [class.tw-border-[var(--sed-black-60-color)]]="tab === 'codes'"
          [class.tw-border-b-white]="tab === 'codes'"
          [class.tw-bg-white]="tab === 'codes'"
          [class.tw-font-semibold]="tab === 'codes'"
          [class.tw-border-transparent]="tab !== 'codes'"
          [class.tw-text-[var(--sed-black-60-color)]]="tab !== 'codes'"
          (click)="tab = 'codes'"
        >
          Codes
        </button>
      </div>

      @if (tab === 'types') {
        <div class="tw-pt-2 tw-space-y-4" [formGroup]="majorGroup">
          @if (loading) {
            <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading claim types…</p>
          } @else {
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <app-select
                [control]="$any(majorGroup.controls['claimTypeCodes'])"
                [options]="claimTypeSelectOptions"
                optionLabel="label"
                optionValue="value"
                label="Claim Types"
                [multiple]="true"
                [chip]="true"
                [multipleFilter]="true"
              />
              <app-select
                [control]="$any(majorGroup.controls['coverages'])"
                [options]="coverageSelectOptions"
                optionLabel="label"
                optionValue="value"
                label="Coverages"
                [multiple]="true"
                [chip]="true"
                [multipleFilter]="true"
              />
            </div>
          }
        </div>
      }

      @if (tab === 'codes') {
        <div class="tw-pt-2 tw-space-y-10" [formGroup]="majorGroup">
          @for (block of codeBlocks; track block.kind) {
            <div class="tw-space-y-3 tw-border-b tw-border-[var(--sed-black-60-color)] tw-pb-6 tw-last:tw-border-0">
              <h4 class="tw-font-semibold tw-text-base">{{ block.title }}</h4>
              <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-end">
                <app-input [control]="block.codeCtrl" label="Code" />
                <app-input [control]="block.descCtrl" label="Description contains" classes="tw-min-w-[200px]" />
                <app-button label="Search" emphasis="Low" [loading]="block.loading" (click)="runSearch(block.kind)" />
              </div>
              <div class="tw-overflow-auto">
                <table class="tw-w-full tw-text-sm tw-border-collapse">
                  <thead>
                    <tr>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Search Results</th>
                      <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Selected</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="tw-align-top tw-pr-2">
                        <label class="tw-sr-only" [attr.for]="'qp-' + formKey + '-' + block.kind + '-search'"
                          >{{ block.title }} search results</label
                        >
                        <select
                          [attr.id]="'qp-' + formKey + '-' + block.kind + '-search'"
                          #avail
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[160px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of availableSearchOptions(block.kind); track o.code) {
                            <option [value]="o.code">{{ codeDashDescriptionLabel(o.code, o.description) }}</option>
                          }
                        </select>
                      </td>
                      <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                        <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                          <app-button label="→" ariaLabel="Add selected" (click)="addCodes(block.kind, avail)" />
                          <app-button label="⇒" ariaLabel="Add all search results" (click)="addAllSearch(block.kind)" />
                          <app-button label="←" ariaLabel="Remove selected" (click)="removeCodes(block.kind, sel)" />
                          <app-button label="⇐" ariaLabel="Remove all selected" (click)="clearCodes(block.kind)" />
                        </div>
                      </td>
                      <td class="tw-align-top tw-pl-2">
                        <label class="tw-sr-only" [attr.for]="'qp-' + formKey + '-' + block.kind + '-selected'"
                          >{{ block.title }} selected codes</label
                        >
                        <select
                          [attr.id]="'qp-' + formKey + '-' + block.kind + '-selected'"
                          #sel
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[160px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (id of selectedIds(block.kind); track id) {
                            <option [value]="id">{{ labelFor(block.kind, id) }}</option>
                          }
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          }
        </div>
      }
    </div>
  `,
})
export class QueryMajorLiabilityLobComponent extends BaseComponent implements OnChanges, OnInit {
  @Input({ required: true }) lobApi!: 'GL' | 'AU' | 'PR';
  @Input({ required: true }) formKey!: 'majorGl' | 'majorAl' | 'majorPl';
  @Input({ required: true }) extended!: FormGroup;
  @Input({ required: true }) contractNumber!: number | null;
  @Input() queryUniqueKey: number | null = null;

  private lastLoadKey: string | null = null;

  tab: LiabilityTab = 'types';
  loading = false;
  claimTypeSelectOptions: { label: string; value: string }[] = [];
  coverageSelectOptions: { label: string; value: string }[] = [];
  private labelByKind: Record<'cause' | 'nature' | 'target', Map<string, string>> = {
    cause: new Map(),
    nature: new Map(),
    target: new Map(),
  };

  readonly codeBlocks: {
    kind: 'cause' | 'nature' | 'target';
    title: string;
    codeCtrl: FormControl<string>;
    descCtrl: FormControl<string>;
    searchOptions: QueryMajorCodeOption[];
    loading: boolean;
  }[] = [
    {
      kind: 'cause',
      title: 'Cause of Loss',
      codeCtrl: new FormControl<string>('', { nonNullable: true }),
      descCtrl: new FormControl<string>('', { nonNullable: true }),
      searchOptions: [],
      loading: false,
    },
    {
      kind: 'nature',
      title: 'Nature/Result',
      codeCtrl: new FormControl<string>('', { nonNullable: true }),
      descCtrl: new FormControl<string>('', { nonNullable: true }),
      searchOptions: [],
      loading: false,
    },
    {
      kind: 'target',
      title: 'Part/Target',
      codeCtrl: new FormControl<string>('', { nonNullable: true }),
      descCtrl: new FormControl<string>('', { nonNullable: true }),
      searchOptions: [],
      loading: false,
    },
  ];

  get title(): string {
    return this.lobApi === 'GL'
      ? 'General Liability'
      : this.lobApi === 'AU'
        ? 'Auto Liability'
        : 'Property';
  }

  get majorGroup(): FormGroup {
    return this.extended.get(this.formKey) as FormGroup;
  }

  ngOnInit(): void {
    void this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contractNumber'] || changes['queryUniqueKey'] || changes['formKey']) {
      this.lastLoadKey = null;
      void this.load();
    }
  }

  selectedIds(kind: 'cause' | 'nature' | 'target'): string[] {
    const key = kind === 'cause' ? 'causeIds' : kind === 'nature' ? 'natureIds' : 'targetIds';
    return (this.majorGroup.get(key)?.value ?? []) as string[];
  }

  /** Search results not yet in the selected list (parity with WC Injury Codes dual-list). */
  availableSearchOptions(kind: 'cause' | 'nature' | 'target'): QueryMajorCodeOption[] {
    const block = this.codeBlocks.find((b) => b.kind === kind);
    if (!block) {
      return [];
    }
    const sel = new Set(this.selectedIds(kind));
    return block.searchOptions.filter((o) => o.code && !sel.has(o.code));
  }

  /**
   * Same display order as WC Injury Codes (`wcInjuryDisplayLabel` on the host): `code - description`.
   */
  codeDashDescriptionLabel(code: string | undefined, description: string | undefined): string {
    const c = code?.trim();
    const d = description?.trim();
    if (c && d) {
      return `${c} - ${d}`;
    }
    if (c) {
      return c;
    }
    if (d) {
      return d;
    }
    return '';
  }

  /**
   * Legacy list order (ordinal `string.CompareTo`):
   * - GL/PR: `GeneralLiability` / `PropertyLiability` `CoverageItemComparer` sorts by `DisplayName`
   *   (`lineCode + "-" + coverageCode + ": " + lineName + " - " + coverageName`).
   * - AU: `AutoLiability` sorts by `Name` (`lineName + " - " + coverageName`).
   */
  private coverageSortKey(o: QueryMajorCoverageOption): string {
    if (this.lobApi === 'AU') {
      return `${o.lineName} - ${o.coverageName}`;
    }
    const displayCode = `${o.lineCode}-${o.coverageCode}`;
    return `${displayCode}: ${o.lineName} - ${o.coverageName}`;
  }

  /** Match C# `string.CompareTo` lexicographic order for coverage sort keys. */
  private compareOrdinalString(a: string, b: string): number {
    return a < b ? -1 : a > b ? 1 : 0;
  }

  /** Coverage row: composite code first, then line/coverage names (parity with `code - description`). */
  private coverageOptionLabel(o: QueryMajorCoverageOption): string {
    if (!o.lineCode && !o.coverageCode && o.value) {
      return o.value;
    }
    // Auto Liability (legacy AutoLiability.CoverageItem): COVERAGECODE key + "lineName - coverageName"
    if (this.lobApi === 'AU') {
      const code = o.coverageCode?.trim() || o.value;
      const legacyName = `${o.lineName} - ${o.coverageName}`.trim();
      if (code && legacyName) {
        return `${code} - ${legacyName}`;
      }
      return legacyName || code || o.value;
    }
    const codePart = `${o.lineCode}|${o.coverageCode}`.trim();
    const namePart = `${o.lineName} / ${o.coverageName}`.trim();
    if (codePart && namePart) {
      return `${codePart} - ${namePart}`;
    }
    return codePart || namePart || o.value;
  }

  labelFor(kind: 'cause' | 'nature' | 'target', id: string): string {
    const m = this.labelByKind[kind];
    const d = m.get(id);
    return this.codeDashDescriptionLabel(id, d) || id;
  }

  async load(): Promise<void> {
    const cn = this.contractNumber;
    if (cn == null || cn <= 0) {
      return;
    }
    const qk = this.queryUniqueKey && this.queryUniqueKey > 0 ? this.queryUniqueKey : undefined;
    const loadKey = `${this.formKey}|${cn}|${qk ?? 0}`;
    if (this.lastLoadKey === loadKey) {
      return;
    }
    this.lastLoadKey = loadKey;
    this.loading = true;
    try {
      let res: QueryMajorLiabilityParametersResponse;
      if (this.lobApi === 'GL') {
        res = await this.api.query.getGlClaimTypes(cn, qk);
      } else if (this.lobApi === 'AU') {
        res = await this.api.query.getAlClaimTypes(cn, qk);
      } else {
        res = await this.api.query.getPlClaimTypes(cn, qk);
      }
      const n = normalizeQueryMajorLiabilityParametersResponse(res, this.lobApi);
      this.claimTypeSelectOptions = (n.claimTypeOptions ?? [])
        .map((o) => ({ label: this.codeDashDescriptionLabel(o.code, o.description), value: o.code }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
      const covOpts = [...(n.coverageOptions ?? [])].sort((a, b) =>
        this.compareOrdinalString(this.coverageSortKey(a), this.coverageSortKey(b)),
      );
      this.coverageSelectOptions = covOpts.map((o) => ({
        label: this.coverageOptionLabel(o),
        value: o.value,
      }));
      this.majorGroup.patchValue(
        {
          claimTypeCodes: [...(n.selectedClaimTypeCodes ?? [])],
          coverages: [...(n.selectedCoverages ?? [])],
        },
        { emitEvent: false },
      );
      this.seedLabels('cause', n.selectedCauses ?? []);
      this.seedLabels('nature', n.selectedNatures ?? []);
      this.seedLabels('target', n.selectedTargets ?? []);
      const includeTypes =
        this.formKey === 'majorGl'
          ? 'includeMajorGlTypes'
          : this.formKey === 'majorAl'
            ? 'includeMajorAlTypes'
            : 'includeMajorPlTypes';
      const includeCodes =
        this.formKey === 'majorGl'
          ? 'includeMajorGlCodes'
          : this.formKey === 'majorAl'
            ? 'includeMajorAlCodes'
            : 'includeMajorPlCodes';
      this.extended.get(includeTypes)?.setValue(true, { emitEvent: false });
      this.extended.get(includeCodes)?.setValue(true, { emitEvent: false });
    } catch (err) {
      console.error(`[QueryMajorLiability] load failed (${this.lobApi})`, err);
      this.claimTypeSelectOptions = [];
      this.coverageSelectOptions = [];
    } finally {
      this.loading = false;
    }
  }

  private seedLabels(kind: 'cause' | 'nature' | 'target', rows: QueryMajorCodeOption[]): void {
    const m = this.labelByKind[kind];
    for (const r of rows) {
      if (r.code) {
        m.set(r.code, r.description ?? r.code);
      }
    }
  }

  async runSearch(kind: 'cause' | 'nature' | 'target'): Promise<void> {
    const cn = this.contractNumber;
    if (cn == null || cn <= 0) {
      return;
    }
    const block = this.codeBlocks.find((b) => b.kind === kind);
    if (!block) {
      return;
    }
    block.loading = true;
    try {
      const res = await this.api.query.searchMajorLiabilityCodes({
        lob: this.lobApi,
        kind,
        contractNumber: cn,
        code: block.codeCtrl.value?.trim() || undefined,
        description: block.descCtrl.value?.trim() || undefined,
      });
      block.searchOptions = [...(res.items ?? [])].sort((a, b) =>
        (a.code ?? '').localeCompare(b.code ?? '', undefined, {
          sensitivity: 'base',
          numeric: true,
        }),
      );
      for (const o of block.searchOptions) {
        if (o.code) {
          this.labelByKind[kind].set(o.code, o.description ?? o.code);
        }
      }
    } finally {
      block.loading = false;
    }
  }

  addCodes(kind: 'cause' | 'nature' | 'target', avail: HTMLSelectElement): void {
    const key = kind === 'cause' ? 'causeIds' : kind === 'nature' ? 'natureIds' : 'targetIds';
    const selected = Array.from(avail.selectedOptions).map((o) => o.value);
    const cur = new Set(this.selectedIds(kind));
    for (const s of selected) {
      cur.add(s);
    }
    this.majorGroup.get(key)?.setValue([...cur]);
    this.markSaved();
  }

  addAllSearch(kind: 'cause' | 'nature' | 'target'): void {
    const block = this.codeBlocks.find((b) => b.kind === kind);
    if (!block) {
      return;
    }
    const key = kind === 'cause' ? 'causeIds' : kind === 'nature' ? 'natureIds' : 'targetIds';
    const cur = new Set(this.selectedIds(kind));
    for (const o of this.availableSearchOptions(kind)) {
      if (o.code) {
        cur.add(o.code);
      }
    }
    this.majorGroup.get(key)?.setValue([...cur].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
    this.markSaved();
  }

  removeCodes(kind: 'cause' | 'nature' | 'target', sel: HTMLSelectElement): void {
    const key = kind === 'cause' ? 'causeIds' : kind === 'nature' ? 'natureIds' : 'targetIds';
    const remove = new Set(Array.from(sel.selectedOptions).map((o) => o.value));
    const next = this.selectedIds(kind).filter((id) => !remove.has(id));
    this.majorGroup.get(key)?.setValue(next);
    this.markSaved();
  }

  clearCodes(kind: 'cause' | 'nature' | 'target'): void {
    const key = kind === 'cause' ? 'causeIds' : kind === 'nature' ? 'natureIds' : 'targetIds';
    this.majorGroup.get(key)?.setValue([]);
    this.markSaved();
  }

  private markSaved(): void {
    const includeTypes =
      this.formKey === 'majorGl'
        ? 'includeMajorGlTypes'
        : this.formKey === 'majorAl'
          ? 'includeMajorAlTypes'
          : 'includeMajorPlTypes';
    const includeCodes =
      this.formKey === 'majorGl'
        ? 'includeMajorGlCodes'
        : this.formKey === 'majorAl'
          ? 'includeMajorAlCodes'
          : 'includeMajorPlCodes';
    this.extended.get(includeTypes)?.setValue(true, { emitEvent: false });
    this.extended.get(includeCodes)?.setValue(true, { emitEvent: false });
  }
}
